import { Book, Heart, Users, Brain, Shield, Zap, ArrowLeft, Camera, Globe, ListMusic, Mic2, Baby, Users2, UserPlus, BookOpen, ArrowRight, Star } from 'lucide-react';
import { Link } from 'wouter';
import { motion } from 'framer-motion';

import Navigation from '@/components/Navigation';
import Footer from '@/components/ui/footer';

export default function About() {
  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50 pt-16">
        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 py-12">
        <Link href="/home">
          <motion.button 
            className="mb-8 flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-teal-600 hover:from-purple-700 hover:to-teal-700 rounded-full shadow-sm hover:shadow-md transition-all text-white font-medium"
            whileHover={{ x: -5 }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </motion.button>
        </Link>

        {/* Hero Section */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.h1 
            className="text-4xl md:text-6xl font-bold mb-4 md:mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <span className="text-snuggle-gradient">
              About SnuggleCrafters.com
            </span>
            <br />
            <span className="text-2xl md:text-3xl text-gray-600 font-normal">by <a href="https://vibecaas.com" className="text-purple-600 hover:text-purple-700">VibeCaaS.com</a></span>
          </motion.h1>
          <motion.p 
            className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed px-4 md:px-0"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            Where AI meets imagination to create magical, personalized bedtime stories that nurture young minds and inspire sweet dreams.
          </motion.p>
        </motion.div>

        {/* Mission Section */}
        <motion.div 
          className="bg-card rounded-2xl md:rounded-3xl shadow-xl p-6 md:p-10 mb-12 md:mb-16 border border-border"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
        >
          <div className="flex items-center gap-3 md:gap-4 mb-6 md:mb-8">
            <div className="bg-gradient-to-r from-purple-600 to-teal-600 p-2.5 md:p-3 rounded-xl md:rounded-2xl shadow-lg">
              <Heart className="w-6 h-6 md:w-8 md:h-8 text-white" />
            </div>
            <h2 className="text-2xl md:text-4xl font-bold text-primary">
              Our Mission
            </h2>
          </div>
          <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4">
            At SnuggleCrafters.com by VibeCaaS.com, we believe every child deserves a unique bedtime story that speaks directly to their interests, 
            age, and the values their parents want to instill. Our mission is to transform bedtime into a magical learning 
            experience that strengthens the parent-child bond while fostering imagination and important life lessons.
          </p>
          <p className="text-base md:text-lg text-gray-700 leading-relaxed">
            Using advanced AI technology, we create stories that are not just entertaining but also educational, 
            helping children drift off to sleep with positive thoughts and valuable lessons that will stay with them for life.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="mb-12 md:mb-16">
          <motion.h2 
            className="text-2xl md:text-4xl font-bold text-center mb-8 md:mb-12 px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <span className="text-primary">
              Why Choose SnuggleCrafters.com?
            </span>
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 px-4 md:px-0">
            <motion.div 
              className="bg-gradient-to-br from-purple-600 to-teal-600 rounded-xl md:rounded-2xl p-5 md:p-6 shadow-sm hover:shadow-xl transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.5 }}
              whileHover={{ y: -5 }}
            >
              <div className="w-10 h-10 md:w-12 md:h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-3 md:mb-4 shadow-lg">
                <img src="/logo.png" alt="SnuggleCrafters Logo" className="w-5 h-5 md:w-6 md:h-6 object-contain" />
              </div>
              <h3 className="text-lg md:text-xl font-semibold text-white mb-2">Personalized Stories</h3>
              <p className="text-sm md:text-base text-white/90">
                Every story is uniquely crafted based on your child's age, interests, and the lessons you want to teach.
              </p>
            </motion.div>

            <motion.div 
              className="bg-gradient-to-br from-teal-600 to-purple-600 rounded-xl md:rounded-2xl p-5 md:p-6 shadow-sm hover:shadow-xl transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0, duration: 0.5 }}
              whileHover={{ y: -5 }}
            >
              <div className="w-10 h-10 md:w-12 md:h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-3 md:mb-4 shadow-lg">
                <Brain className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </div>
              <h3 className="text-lg md:text-xl font-semibold text-white mb-2">AI-Powered Creativity</h3>
              <p className="text-sm md:text-base text-white/90">
                Powered by VibeCaaS.com models, our stories are creative, engaging, and always age-appropriate.
              </p>
            </motion.div>

            <motion.div 
              className="bg-gradient-to-br from-purple-600 to-amber-600 rounded-xl md:rounded-2xl p-5 md:p-6 shadow-sm hover:shadow-xl transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.5 }}
              whileHover={{ y: -5 }}
            >
              <div className="w-10 h-10 md:w-12 md:h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-3 md:mb-4 shadow-lg">
                <Book className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </div>
              <h3 className="text-lg md:text-xl font-semibold text-white mb-2">Story Library</h3>
              <p className="text-sm md:text-base text-white/90">
                Save and revisit all your generated stories. Build a personal library of bedtime tales.
              </p>
            </motion.div>

            <motion.div 
              className="bg-gradient-to-br from-teal-600 to-blue-600 rounded-xl md:rounded-2xl p-5 md:p-6 shadow-sm hover:shadow-xl transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.5 }}
              whileHover={{ y: -5 }}
            >
              <div className="w-10 h-10 md:w-12 md:h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-3 md:mb-4 shadow-lg">
                <Shield className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </div>
              <h3 className="text-lg md:text-xl font-semibold text-white mb-2">Safe & Appropriate</h3>
              <p className="text-sm md:text-base text-white/90">
                All content is carefully generated to be child-safe, positive, and appropriate for bedtime.
              </p>
            </motion.div>

            <motion.div 
              className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl md:rounded-2xl p-5 md:p-6 shadow-sm hover:shadow-xl transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3, duration: 0.5 }}
              whileHover={{ y: -5 }}
            >
              <div className="w-10 h-10 md:w-12 md:h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-3 md:mb-4 shadow-lg">
                <Zap className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </div>
              <h3 className="text-lg md:text-xl font-semibold text-white mb-2">Text-to-Speech</h3>
              <p className="text-sm md:text-base text-white/90">
                Listen to stories with natural, soothing voices perfect for bedtime storytelling.
              </p>
            </motion.div>

            <motion.div 
              className="bg-gradient-to-br from-rose-600 to-pink-600 rounded-xl md:rounded-2xl p-5 md:p-6 shadow-sm hover:shadow-xl transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4, duration: 0.5 }}
              whileHover={{ y: -5 }}
            >
              <div className="w-10 h-10 md:w-12 md:h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-3 md:mb-4 shadow-lg">
                <Camera className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </div>
              <h3 className="text-lg md:text-xl font-semibold text-white mb-2">Character Studio Add-on</h3>
              <p className="text-sm md:text-base text-white/90">
                Premium subscription add-on to transform family photos into magical cartoon characters that star in your personalized story illustrations.
              </p>
            </motion.div>

            <motion.div 
              className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl md:rounded-2xl p-5 md:p-6 shadow-sm hover:shadow-xl transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.5, duration: 0.5 }}
              whileHover={{ y: -5 }}
            >
              <div className="w-10 h-10 md:w-12 md:h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-3 md:mb-4 shadow-lg">
                <Mic2 className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </div>
              <h3 className="text-lg md:text-xl font-semibold text-white mb-2">Multi-Voice Narration</h3>
              <p className="text-sm md:text-base text-white/90">
                Different voices for each character - narrator, heroes, and villains get unique voices for enhanced storytelling.
              </p>
            </motion.div>

            <motion.div 
              className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-xl md:rounded-2xl p-5 md:p-6 shadow-sm hover:shadow-xl transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.6, duration: 0.5 }}
              whileHover={{ y: -5 }}
            >
              <div className="w-10 h-10 md:w-12 md:h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-3 md:mb-4 shadow-lg">
                <Globe className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </div>
              <h3 className="text-lg md:text-xl font-semibold text-white mb-2">10 Languages Supported</h3>
              <p className="text-sm md:text-base text-white/90">
                Create stories in English, Spanish, French, German, Chinese, Japanese, Portuguese, Italian, Russian, and Arabic.
              </p>
            </motion.div>

            <motion.div 
              className="bg-gradient-to-br from-violet-600 to-fuchsia-600 rounded-xl md:rounded-2xl p-5 md:p-6 shadow-sm hover:shadow-xl transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.7, duration: 0.5 }}
              whileHover={{ y: -5 }}
            >
              <div className="w-10 h-10 md:w-12 md:h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-3 md:mb-4 shadow-lg">
                <ListMusic className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </div>
              <h3 className="text-lg md:text-xl font-semibold text-white mb-2">Story Playlists</h3>
              <p className="text-sm md:text-base text-white/90">
                Create custom playlists with auto-play mode for uninterrupted bedtime routines. Share playlists publicly.
              </p>
            </motion.div>

            <motion.div 
              className="bg-gradient-to-br from-amber-600 to-purple-600 rounded-xl md:rounded-2xl p-5 md:p-6 shadow-sm hover:shadow-xl transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.8, duration: 0.5 }}
              whileHover={{ y: -5 }}
            >
              <div className="w-10 h-10 md:w-12 md:h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-3 md:mb-4 shadow-lg">
                <img src="/logo.png" alt="SnuggleCrafters Logo" className="w-5 h-5 md:w-6 md:h-6 object-contain" />
              </div>
              <h3 className="text-lg md:text-xl font-semibold text-white mb-2">Educational Value</h3>
              <p className="text-sm md:text-base text-white/90">
                Each story includes valuable life lessons chosen by parents to help children grow and learn.
              </p>
            </motion.div>
          </div>
        </div>

        {/* How Child Profiles Power Personalization */}
        <motion.div 
          className="bg-gradient-to-br from-rose-50 via-pink-50 to-purple-50 rounded-2xl md:rounded-3xl shadow-xl p-6 md:p-10 mb-12 md:mb-16 border border-rose-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.9, duration: 0.5 }}
          data-testid="child-profiles-section"
        >
          <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
            {/* Left side - Icon and Content */}
            <div className="flex-1">
              <div className="flex items-center gap-3 md:gap-4 mb-4 md:mb-6">
                <div className="bg-gradient-to-r from-rose-500 to-pink-500 p-2.5 md:p-3 rounded-xl md:rounded-2xl shadow-lg">
                  <Baby className="w-6 h-6 md:w-8 md:h-8 text-white" />
                </div>
                <h2 className="text-2xl md:text-4xl font-bold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
                  How Child Profiles Power Personalization
                </h2>
              </div>
              
              <p className="text-sm md:text-base text-gray-600 mb-3 md:mb-4 italic">
                Your child's details and photo become the foundation for truly personal stories
              </p>

              <div className="space-y-3 md:space-y-4" data-testid="child-profiles-content">
                <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                  <strong>Save Essential Information:</strong> Child profiles store all the details that make stories personal - 
                  your child's name, age, birthday, gender, and preferred language. This information becomes the building block 
                  for creating stories that feel like they were written just for them.
                </p>
                <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                  <strong>Photo to Cartoon Magic:</strong> Upload your child's photo and watch our AI transform it into a 
                  delightful cartoon avatar. This custom character becomes their story companion, making every tale even more 
                  special and engaging.
                </p>
                <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                  <strong>Seamless Story Creation:</strong> Profile data automatically fills the story wizard, saving you time. 
                  Best of all, their cartoon character stars in the story illustrations, bringing smiles and wonder as they 
                  see themselves on magical adventures.
                </p>
              </div>
            </div>

            {/* Right side - Workflow Diagram */}
            <div className="flex-1 flex items-center justify-center" data-testid="profile-workflow">
              <div className="w-full max-w-md">
                {/* Mobile: Vertical Stack */}
                <div className="flex flex-col md:hidden gap-4">
                  <motion.div 
                    className="bg-white rounded-xl p-4 shadow-lg border border-rose-200"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 2.0, duration: 0.5 }}
                    data-testid="workflow-step-1"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-gradient-to-r from-rose-500 to-pink-500 rounded-lg flex items-center justify-center">
                        <UserPlus className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="font-bold text-gray-800">Create Profile</h3>
                    </div>
                    <p className="text-sm text-gray-600">Save child details + upload photo</p>
                  </motion.div>
                  
                  <div className="flex justify-center">
                    <ArrowRight className="w-6 h-6 text-rose-400 rotate-90" />
                  </div>

                  <motion.div 
                    className="bg-white rounded-xl p-4 shadow-lg border border-pink-200"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 2.1, duration: 0.5 }}
                    data-testid="workflow-step-2"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-gradient-to-r from-pink-500 to-purple-500 rounded-lg flex items-center justify-center">
                        <Camera className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="font-bold text-gray-800">AI Generates Avatar</h3>
                    </div>
                    <p className="text-sm text-gray-600">Cartoon character created automatically</p>
                  </motion.div>
                  
                  <div className="flex justify-center">
                    <ArrowRight className="w-6 h-6 text-pink-400 rotate-90" />
                  </div>

                  <motion.div 
                    className="bg-white rounded-xl p-4 shadow-lg border border-purple-200"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 2.2, duration: 0.5 }}
                    data-testid="workflow-step-3"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="font-bold text-gray-800">Stories Come Alive</h3>
                    </div>
                    <p className="text-sm text-gray-600">Character stars in personalized illustrations</p>
                  </motion.div>
                </div>

                {/* Desktop: Horizontal Flow */}
                <div className="hidden md:flex flex-row items-center gap-4">
                  <motion.div 
                    className="bg-white rounded-xl p-5 shadow-lg border border-rose-200"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 2.0, duration: 0.5 }}
                    data-testid="workflow-step-1-desktop"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 bg-gradient-to-r from-rose-500 to-pink-500 rounded-lg flex items-center justify-center">
                        <UserPlus className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="font-bold text-gray-800 text-lg">Create Profile</h3>
                    </div>
                    <p className="text-sm text-gray-600">Save child details + upload photo</p>
                  </motion.div>
                  
                  <div className="flex justify-center">
                    <ArrowRight className="w-8 h-8 text-rose-400" />
                  </div>

                  <motion.div 
                    className="bg-white rounded-xl p-5 shadow-lg border border-pink-200"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 2.1, duration: 0.5 }}
                    data-testid="workflow-step-2-desktop"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-purple-500 rounded-lg flex items-center justify-center">
                        <Camera className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="font-bold text-gray-800 text-lg">AI Generates Avatar</h3>
                    </div>
                    <p className="text-sm text-gray-600">Cartoon character created automatically</p>
                  </motion.div>
                  
                  <div className="flex justify-center">
                    <ArrowRight className="w-8 h-8 text-pink-400" />
                  </div>

                  <motion.div 
                    className="bg-white rounded-xl p-5 shadow-lg border border-purple-200"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 2.2, duration: 0.5 }}
                    data-testid="workflow-step-3-desktop"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                        <BookOpen className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="font-bold text-gray-800 text-lg">Stories Come Alive</h3>
                    </div>
                    <p className="text-sm text-gray-600">Character stars in personalized illustrations</p>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* How It Works */}
        <motion.div 
          className="bg-card rounded-3xl shadow-xl p-10 mb-16 border border-border"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.5 }}
        >
          <h2 className="text-4xl font-bold text-center mb-12">
            <span className="text-primary">
              How It Works
            </span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.6, duration: 0.5 }}
            >
              <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-teal-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold shadow-lg">
                1
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Create a Child Profile</h3>
              <p className="text-sm text-gray-600">Enter their name, birthday, gender, and language</p>
            </motion.div>
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.7, duration: 0.5 }}
            >
              <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-teal-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold shadow-lg">
                2
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Choose Interests</h3>
              <p className="text-sm text-gray-600">Select what sparks their imagination</p>
            </motion.div>
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.8, duration: 0.5 }}
            >
              <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-teal-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold shadow-lg">
                3
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Pick a Style</h3>
              <p className="text-sm text-gray-600">Funny, magical, or calming</p>
            </motion.div>
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.9, duration: 0.5 }}
            >
              <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-teal-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold shadow-lg">
                4
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Add a Lesson</h3>
              <p className="text-sm text-gray-600">What should they learn?</p>
            </motion.div>
            <motion.div 
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 2.0, duration: 0.5 }}
            >
              <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-teal-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold shadow-lg">
                ✓
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Enjoy Your Story</h3>
              <p className="text-sm text-gray-600">Read or listen together</p>
            </motion.div>
          </div>
        </motion.div>



        {/* Parent Companies */}
        <div className="bg-gradient-to-r from-purple-600 to-teal-600 rounded-3xl p-8 text-white mb-12">
          <h2 className="text-3xl font-bold mb-6 text-center">Powered by Innovation</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="text-center">
              <h3 className="text-2xl font-mono font-bold mb-4"><a href="https://vibecaas.com" className="text-purple-100 hover:text-purple-200">VibeCaaS.com</a></h3>
              <p className="leading-relaxed">
                The AI-powered development platform that redefines how businesses 
                and creators build with technology. From concept to deployment, we make 
                advanced AI development seamless, scalable, and accessible for everyone.
              </p>
            </div>
            <div className="text-center">
              <h3 className="text-2xl font-mono font-bold mb-4"><a href="https://neuralquantum.ai" className="text-teal-100 hover:text-teal-200">NeuralQuantum.ai</a></h3>
              <p className="leading-relaxed">
                Pioneering the intersection of quantum computing and artificial intelligence to 
                create next-generation solutions that push the boundaries of what's possible.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <motion.div 
          className="text-center bg-white rounded-3xl shadow-xl p-10 border border-purple-100"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 2.5, duration: 0.5 }}
        >
          <h2 className="text-4xl font-bold mb-6">
            <span className="text-primary">
              Get in Touch
            </span>
          </h2>
          <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
            We'd love to hear from you! Whether you have feedback, suggestions, or just want to share 
            how SnuggleCrafters.com by VibeCaaS.com has become part of your bedtime routine.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.a 
              href="mailto:support@SnuggleCrafters.com" 
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-teal-600 hover:from-purple-700 hover:to-teal-700 text-white rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 font-medium"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Contact Support
            </motion.a>
            <motion.a 
              href="mailto:support@SnuggleCrafters.com" 
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-teal-600 hover:from-purple-700 hover:to-teal-700 text-white rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 font-medium"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Send Feedback
            </motion.a>
          </div>
        </motion.div>

        {/* Multi-Child Family Testimonial */}
        <motion.div 
          className="bg-gradient-to-br from-purple-600 via-pink-500 to-rose-500 rounded-2xl md:rounded-3xl shadow-2xl p-6 md:p-10 border border-white/20"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 2.6, duration: 0.5 }}
          data-testid="multi-child-testimonial"
        >
          <div className="flex flex-col md:flex-row gap-6 items-center">
            {/* Icon */}
            <div className="flex-shrink-0">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-xl">
                <Users className="w-8 h-8 md:w-10 md:h-10 text-white" />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 text-center md:text-left">
              {/* Star Rating */}
              <div className="flex justify-center md:justify-start gap-1 mb-3" data-testid="testimonial-rating">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 md:w-6 md:h-6 fill-yellow-300 text-yellow-300" />
                ))}
              </div>

              {/* Testimonial Content */}
              <p className="text-base md:text-lg text-white leading-relaxed mb-4" data-testid="testimonial-content">
                "With profiles for all three kids (ages 3, 6, and 9), each child gets stories perfectly tailored to their age and interests. 
                The photo-to-avatar feature is magical - they love seeing themselves as cartoon characters! Worth every penny for our family."
              </p>

              {/* Author and Badge */}
              <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-3">
                <div data-testid="testimonial-author">
                  <p className="text-white font-bold text-lg">Maria & David Rodriguez</p>
                  <p className="text-white/80 text-sm">Parents of 3</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs md:text-sm font-medium border border-white/30" data-testid="testimonial-badge">
                    ✓ Verified Multi-Child Family
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <Footer />
      </div>
    </>
  );
}